import { DialogContent, DialogTitle, Stack, Typography, Box } from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import { AttachMoney } from "@mui/icons-material";

import { PatchAppointmentStatus } from "../../../services/fhir";
import { CommonDialog } from "../../../components/common/CommonDialog";
import { FormButtonGroup } from "../../../components/form";
import { DeleteWhereby } from "../../../services/whereby/DeleteWhereby";
import { PutPaymentNotice } from "@/services/fhir/paymentnotice/PutPaymentNotice";
import { useDeleteEvent } from "../../../services/google/DeleteEvent";
import { useSessionContext } from "../../../contexts/SessionContext";

interface IProps {
  setModalOpen: (value: boolean) => void;
  open: boolean;
  row: any;
  payment: any;
  isPaid: boolean;
}

export function NextAppointmentsDialog({ setModalOpen, open, row, payment, isPaid }: IProps) {
  const [loading, setLoading] = useState(false);
  const { deleteEvent } = useDeleteEvent();
  const { user, accessTokenGoogle } = useSessionContext();

  // Função para excluir evento do Google Calendar
  const removeFromGoogleCalendar = async (appointment: any) => {
    const googleCalendarExt = appointment?.extension?.find((ext: any) =>
      ext.url === "https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId"
    );

    if (
      googleCalendarExt?.valueString &&
      accessTokenGoogle &&
      user?.refreshTokenGoogle
    ) {
      try {
        await deleteEvent({
          eventId: googleCalendarExt.valueString,
          accessTokenGoogle,
          refreshToken: user.refreshTokenGoogle,
        });
        return true;
      } catch (err) {
        console.error("Erro ao excluir evento do Google Calendar:", err);
        return false;
      }
    }
    return false;
  };

  const handleCancelAppointment = async (data: any) => {
    if (payment?.[0]?.resource?.paymentStatus?.coding?.[0]?.code === 'cleared') {
      toast.error("Não é possível cancelar uma consulta que já foi paga");
      setModalOpen(false);
      return;
    }

    setLoading(true);
    try {
      if (payment?.length > 0) {
        const paymentCopy = { ...payment?.[0]?.resource };
        paymentCopy.status = "cancelled";
        await PutPaymentNotice({
          paymentId: payment?.[0]?.resource?.id,
          data: paymentCopy,
        });
      }

      // Tenta remover do Google Calendar antes de cancelar a consulta
      const appointmentToUse = data?.auxCancelAppointment || data?.appointment;
      if (appointmentToUse) {
        await removeFromGoogleCalendar(appointmentToUse);
      }

      await PatchAppointmentStatus({
        appointmentId: data?.auxCancelAppointment.id,
        status: "cancelled",
      });
      row.setTabRefetch((prev: boolean) => !prev);

      // Validação para deletar a consulta no Whereby tanto na agenda do profissional quanto na agenda do manager
      if (data?.auxCancelAppointment?.wherebyId) {
        DeleteWhereby(data?.auxCancelAppointment?.wherebyId);
      } else if (data?.appointment?.virtualService) {
        DeleteWhereby(
          data?.appointment?.virtualService?.[0]?.sessionKey as string
        );
      }

      toast.success("Consulta cancelada com sucesso!");
      setModalOpen(false);
    } catch (err) {
      toast.error("Não foi possível cancelar a consulta selecionada!");
    } finally {
      setLoading(false);
    }
  };

  const hasPaymentStatus = payment?.[0]?.resource?.paymentStatus?.coding?.[0]?.code === "cleared";

  return (
    <CommonDialog handleClose={() => setModalOpen(false)} isOpen={open}>
      <DialogTitle textAlign="center">
        <Typography variant="h5" fontWeight={600}>
          Cancelar consulta
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack
          height="100%"
          direction="column"
          alignItems="center"
          justifyContent="space-between"
        >
          {hasPaymentStatus ? (
            <Stack alignItems="center">
              <Stack direction="row" spacing={2} alignItems="center" py={2}>
                <Box
                  sx={{
                    backgroundColor: "warning100.main",
                    borderRadius: "50%",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AttachMoney sx={{ color: "warning700.main", fontSize: 32 }} />
                </Box>
                <Typography
                  variant="h6"
                  color="warning700.main"
                  textAlign="center"
                >
                  Consulta já paga
                </Typography>
              </Stack>
              <Typography
                variant="body1"
                color="neutral700.main"
                textAlign="center"
              >
                Não é possível cancelar uma consulta com pagamento já confirmado.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="subtitle2">
              Deseja confirmar a operação?
            </Typography>
          )}
          <Stack
            direction="row"
            justifyContent="space-around"
            width="100%"
            mt={3}
          >
            <FormButtonGroup
              onGoBackButtonClick={() => setModalOpen(false)}
              onNextButtonClick={() => handleCancelAppointment(row)}
              goBackButtonText={hasPaymentStatus ? "Fechar" : "Não"}
              nextButtonText="Sim"
              loading={loading}
              disabled={hasPaymentStatus}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </CommonDialog>
  );
}
